import React, { useState, useMemo, useEffect, useRef } from "react";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "../components/ui/table";
import Button from "../components/ui/button/Button";
import InputField from "../components/ui/input/InputField";
import { Plus, IndianRupee, Loader2, X, Check, Edit2, Save, AlertCircle,Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import budgetService from "../api/budgetService";

export default function BudgetRangesSettings({ isDark, activeFilters, onDataLoaded }) {
  const [ranges, setRanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [isAdding, setIsAdding] = useState(false);
  const [newRangeLabel, setNewRangeLabel] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);

  const hasFetched = useRef(false);

  // Design tokens: Slate 300 (Dark) and Slate 600 (Light)
  const secondaryTextColor = isDark ? 'text-slate-300' : 'text-slate-600';
  const labelStyle = `${secondaryTextColor} font-bold uppercase tracking-widest`;

  /**
   * Helper to format input strings. 
   * Converts "0 to 100" or "500" into "₹0 to ₹100" or "₹500"
   */
  const formatWithRupee = (val) => {
    if (!val) return "";
    return val.replace(/(?<!₹)(\d+)/g, '₹$1');
  };

  // --- API Handlers ---
  const toastStyle = {
    style: {
      borderRadius: '12px',
      background: isDark ? '#020617' : '#ffffff', 
      color: isDark ? '#cbd5e1' : '#475569', // Slate 300 / 600
      border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
      fontSize: '11px',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontWeight: '800',
      padding: '12px 20px',
    },
  };

  const fetchRanges = async () => {
    setLoading(true);
    try {
      const data = await budgetService.getAdminBudgets();
      const rangesData = Array.isArray(data) ? data : (data?.data || []);
      
      // Initial Sort: Active first, then Alphabetical
      const sorted = [...rangesData].sort((a, b) => {
        if (a.is_active !== b.is_active) return b.is_active - a.is_active;
        return a.range_label.localeCompare(b.range_label);
      });
      
      setRanges(sorted);
      if (onDataLoaded) onDataLoaded(sorted);
    } catch (err) {
      setError("Failed to load budget ranges");
      toast.error("Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasFetched.current) {
      fetchRanges();
      hasFetched.current = true;
    }
  }, []);

  const handleCreate = async () => {
    if (!newRangeLabel.trim()) return toast.error("RANGE LABEL REQUIRED", toastStyle);
    
    const formattedLabel = formatWithRupee(newRangeLabel);
    setSaving(true);

    // 2. USE PROMISE FOR AUTO-DISMISSING TOASTS
    toast.promise(
      budgetService.createBudget(formattedLabel),
      {
        loading: 'SAVING NEW RANGE...',
        success: () => {
          setNewRangeLabel("");
          setIsAdding(false);
          fetchRanges();
          return 'BUDGET RANGE ADDED';
        },
        error: 'FAILED TO SAVE RANGE',
      },
      toastStyle
    );
    setSaving(false);
  };

  const saveEdit = async (range) => {
    if (!editValue.trim()) return toast.error("VALUE CANNOT BE EMPTY", toastStyle);
    const formattedLabel = formatWithRupee(editValue);

    toast.promise(
      budgetService.updateBudget(range.id, {
        range_label: formattedLabel,
        is_active: range.is_active
      }),
      {
        loading: 'UPDATING...',
        success: () => {
          setRanges(prev => prev.map(r => r.id === range.id ? { ...r, range_label: formattedLabel } : r));
          setEditingId(null);
          return 'UPDATED SUCCESSFULLY';
        },
        error: 'UPDATE FAILED',
      },
      toastStyle
    );
  };

  const toggleStatus = async (range) => {
    const newStatus = range.is_active === 1 ? 0 : 1;
    const statusText = newStatus === 1 ? 'ACTIVATED' : 'HIDDEN';

    // Perform the API call
    const updatePromise = budgetService.updateBudget(range.id, { 
      range_label: range.range_label, 
      is_active: newStatus 
    });

    // Use toast.promise to match the successful file's behavior
    toast.promise(updatePromise, {
      loading: `UPDATING STATUS...`,
      success: () => {
        // This local state update is what prevents the row from jumping
        setRanges(prev => prev.map(r => r.id === range.id ? { ...r, is_active: newStatus } : r));
        return `RANGE ${statusText}`;
      },
      error: "STATUS UPDATE FAILED",
    }, toastStyle);
  };

  const startEditing = (range) => {
    setEditingId(range.id);
    setEditValue(range.range_label);
  };

  const handlePurge = async (range) => {
    // 1. Fetch count of enquiries using this budget
    const stats = await budgetService.getBudgetStats(range.id);
    const count = stats?.enquiry_count || 0;

    // 2. Show confirmation toast
    toast((t) => (
      <div className="flex flex-col gap-3">
        <div className="space-y-1">
          <p className="font-bold text-[10px] tracking-widest text-orange-600 uppercase">Confirm Budget Purge</p>
          <p className="text-[11px] text-slate-500 leading-normal normal-case">
            Warning: This will also delete <span className="text-orange-600 font-bold">{count} enquiries</span> linked to this range.
          </p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={async () => {
              toast.dismiss(t.id);
              const tid = toast.loading("PURGING BUDGET...", toastStyle);
              try {
                await budgetService.purgeBudget(range.id);
                setRanges(prev => prev.filter(r => r.id !== range.id));
                toast.success(`PURGED ${range.range_label} & ${count} ENQUIRIES`, { ...toastStyle, id: tid });
              } catch (err) {
                toast.error("PURGE FAILED", { ...toastStyle, id: tid });
              }
            }} 
            className="bg-orange-600 text-white px-3 py-1.5 rounded-lg text-[10px] font-black uppercase shadow-lg active:scale-95"
          >
            Confirm Wipeout
          </button>
          <button 
            onClick={() => toast.dismiss(t.id)} 
            className="bg-slate-700 text-white px-3 py-1.5 rounded-lg text-[10px] font-black uppercase opacity-70 hover:opacity-100"
          >
            Cancel
          </button>
        </div>
      </div>
    ), { 
      ...toastStyle, 
      duration: 8000, 
      icon: <Trash2 size={24} className="text-orange-500 shrink-0" /> 
    });
  };

  // --- Filtering Logic (Stays stuck on toggle) ---
  const filteredAndSorted = useMemo(() => {
    let result = [...ranges];
    
    const searchTerm = (activeFilters?.global_search?.[0] || "").toLowerCase();
    if (searchTerm) {
      result = result.filter(r => r.range_label?.toLowerCase().includes(searchTerm));
    }

    const statusFilters = activeFilters?.is_active || [];
    if (statusFilters.length > 0) {
      result = result.filter(r => {
        const statusString = r.is_active === 1 ? "Active" : "Inactive";
        return statusFilters.includes(statusString);
      });
    }

    // Only perform the "Active at top" sort on initial load or search change.
    // We avoid sorting if only the 'is_active' status of an item changed.
    if (hasFetched.current && !searchTerm && statusFilters.length === 0) {
      return result;
    }

    return result.sort((a, b) => b.is_active - a.is_active);
  }, [ranges, activeFilters?.global_search, activeFilters?.is_active]);

  return (
    <div className={`p-1 sm:p-6 space-y-6 min-h-screen transition-colors duration-300 ${isDark ? 'bg-slate-800/0' : 'bg-slate-100/0'}`}>
      {/* HEADER CONTAINER - CENTERED ALIGNMENT */}
{/* HEADER CONTAINER - Consistent with other modules */}
<div className="relative flex flex-col items-center justify-center min-h-[120px] sm:min-h-[100px] sm:-mt-8 max-w-7xl mx-auto w-full px-4">
  <div className="text-center">
    {/* 1. MAIN HEADER: Action in Orange, Entity in Blue */}
    <h2 className={`text-2xl font-brand-heading font-bold uppercase tracking-tighter leading-tight ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
      <span className="text-orange-600">Budget</span> <span className={isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]'}> Ranges</span>
    </h2>
    
    {/* 2. SUBHEADER: Added standard dividers */}
    <div className="flex items-center justify-center gap-3 mt-0">
      <div className={`h-px w-4 ${isDark ? 'bg-slate-400' : 'bg-slate-400'}`} />
      <p className={`text-[13px] uppercase tracking-[0.1em] ${secondaryTextColor}`}>
        Monetary Tiers & Estimates
      </p>
      <div className={`h-px w-4 ${isDark ? 'bg-slate-400' : 'bg-slate-400'}`} />
    </div>
  </div>
  
  {/* Button Wrapper: Context-aware (New Range / Cancel) */}
  <div className="mt-4 sm:mt-0 sm:absolute sm:right-4 sm:top-1/2 sm:-translate-y-1/2 flex items-center">
<Button 
  isDark={isDark} 
  onClick={() => setIsAdding(!isAdding)}
  className={`
    group flex items-center px-5 py-2.5 rounded-xl font-bold uppercase tracking-wider text-xs transition-all border shadow-sm hover:shadow-md active:scale-95 whitespace-nowrap
    ${isDark 
      ? 'bg-slate-900/50 border-slate-700 hover:bg-slate-800' 
      : 'bg-white border-slate-200 hover:bg-slate-50'}
  `}
>
  {/* Conditional Icon with Orange Accent and Animation */}
  {isAdding ? (
    <X 
      size={18} 
      strokeWidth={3} 
      className="text-orange-600 transition-transform duration-300 group-hover:scale-125" 
    />
  ) : (
    <Plus 
      size={18} 
      strokeWidth={3} 
      className="text-orange-600 transition-transform duration-500 ease-out group-hover:rotate-90" 
    />
  )}

  {/* Consistent Blue Text */}
  <span className={`ml-2 ${isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]'}`}>
    {isAdding ? "Cancel" : "New Range"}
  </span>
</Button>
  </div>
</div>

      {/* Inline Add Form */}
      {isAdding && (
        <div className={`max-w-7xl mx-auto w-full p-6 rounded-2xl border mb-6 animate-in slide-in-from-top-4 duration-300 ${
          isDark ? 'bg-slate-900 border-indigo-500/30' : 'bg-white border-indigo-200'
        }`}>
          <div className="flex flex-col sm:flex-row items-end gap-4">
            <div className="w-full flex-1 space-y-2">
               <label className={`block ml-1 text-[11px] ${labelStyle}`}>
                Range Label
              </label>
              <InputField 
                isDark={isDark} 
                placeholder="e.g. 5000 - 10000 (Symbol added automatically)" 
                value={newRangeLabel}
                onChange={(e) => setNewRangeLabel(e.target.value)}
                autoFocus
              />
            </div>
<Button 
  isDark={isDark} 
  onClick={handleCreate} 
  disabled={saving} 
  className={`
    group flex items-center justify-center px-6 py-2.5 rounded-xl font-bold uppercase tracking-wider text-xs transition-all border shadow-sm hover:shadow-md active:scale-95 whitespace-nowrap w-full sm:w-auto
    ${isDark 
      ? 'bg-slate-900/50 border-slate-700 hover:bg-slate-800 disabled:opacity-50' 
      : 'bg-white border-slate-200 hover:bg-slate-50 disabled:opacity-50'}
  `}
>
  {/* Conditional Icon: Loader or Animated Save */}
  {saving ? (
    <Loader2 className="animate-spin text-slate-500" size={18} />
  ) : (
    <Save 
      size={18} 
      strokeWidth={2} 
      className="text-orange-600 transition-transform duration-300 group-hover:scale-110" 
    />
  )}

  {/* Blue Text */}
  <span className={`ml-2 ${isDark ? 'text-[#5A9AFF]' : 'text-[#2D4D94]'}`}>
    {saving ? "Adding..." : "Add Range"}
  </span>
</Button>
          </div>
        </div>
      )}

      {error && (
        <div className="max-w-7xl mx-auto w-full p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 text-red-500 text-sm">
          <AlertCircle size={18} /> {error}
        </div>
      )}

      {/* Table Container */}
      <div className={`rounded-2xl border shadow-sm overflow-hidden max-w-7xl mx-auto w-full transition-all ${
        isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'
      }`}>
        <div className="overflow-x-auto w-full">
          {loading && ranges.length === 0 ? (
            <div className="py-20 flex flex-col items-center justify-center gap-4">
              <Loader2 className="animate-spin text-indigo-500" size={32} />
              <span className={`uppercase tracking-[0.2em] text-[10px] font-brand-heading font-bold ${secondaryTextColor}`}>Loading Budgets...</span>
            </div>
          ) : (
            <Table className="min-w-[800px] w-full text-left">
              <TableHeader className={isDark ? 'bg-slate-950/50' : 'bg-slate-50/50'}>
                <TableRow className={isDark ? 'border-slate-800' : 'border-slate-100'}>
                  <TableCell isHeader className={`px-6 py-4 text-[11px] font-black font-brand-heading uppercase tracking-widest ${secondaryTextColor}`}>Budget Tier</TableCell>
                  <TableCell isHeader className={`px-6 py-4 text-[11px] font-black font-brand-heading uppercase tracking-widest ${secondaryTextColor}`}>Status</TableCell>
                  <TableCell isHeader className="px-6 py-4 text-right"></TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSorted.map((range) => (
                  <TableRow 
                    key={range.id} 
                    className={`transition-all group border-b last:border-none ${
                      isDark ? 'hover:bg-slate-800/50 border-slate-800' : 'hover:bg-indigo-50/30 border-slate-50'
                    }`}
                  >
                    <TableCell className="px-6 py-5">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center border transition-colors ${
                          isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'
                        }`}>
                          <IndianRupee size={20} className={range.is_active === 1 ? "text-[#2d4e94]" : "text-orange-300"} />
                        </div>
                        
                        {editingId === range.id ? (
                          <div className="flex items-center gap-2 flex-1 max-w-xs">
                            <input 
                              className={`w-full bg-transparent border-b-2 border-indigo-500 outline-none font-brand-body-bold text-sm py-1 ${isDark ? 'text-white' : 'text-slate-900'}`}
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              autoFocus
                            />
                          </div>
                        ) : (
                          <div className={`uppercase tracking-tight text-sm font-brand-body-bold ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
                            {range.range_label}
                          </div>
                        )}
                      </div>
                    </TableCell>

                    <TableCell className="px-6 py-5">
                      <div className="flex items-center gap-4">
                        <span className={`text-[11px] font-bold  tracking-widest w-16 ${range.is_active === 1 ? 'text-[#2d4e94]' : 'text-slate-400'}`}>
                          {range.is_active === 1 ? 'Active' : 'Hidden'}
                        </span>
                        <button
                          type="button"
                          onClick={() => toggleStatus(range)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none shadow-inner cursor-pointer ${
                            range.is_active === 1 ? 'bg-[#2d4e94c5]' : 'bg-orange-200'
                          }`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-300 ${range.is_active === 1 ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                    </TableCell>

                    <TableCell className="px-6 py-5 text-right">
                      <div className="flex justify-end gap-2">
                        {editingId === range.id ? (
                          <button onClick={() => saveEdit(range)} className="p-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-all">
                            <Check size={16} />
                          </button>
                        ) : (
                          <>
                            <button onClick={() => startEditing(range)} className={`p-2 rounded-lg border transition-all ${isDark ? 'border-slate-700 text-slate-400 hover:text-white hover:bg-slate-700' : 'border-slate-200 text-slate-400 hover:text-indigo-600 hover:bg-slate-50'}`}>
                              <Edit2 size={16} />
                            </button>
                            <button 
                              onClick={() => handlePurge(range)} 
                              className={`p-2 rounded-lg border transition-all ${isDark ? 'border-slate-700 text-orange-600 hover:bg-rose-500/10' : 'border-slate-200 text-orange-500 hover:bg-rose-50'}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredAndSorted.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={3} className="py-10 text-center">
                      <p className={`text-xs uppercase tracking-widest ${secondaryTextColor}`}>No ranges found matching filters</p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>

      </div>
    </div>
  );
}